// src/pages/BlogPost.js
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiClock, FiCalendar } from 'react-icons/fi';
import SEO from '../components/SEO';
import NewsletterSignup from '../components/NewsletterSignup';
import { blogPosts } from '../data/blogPosts';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-off-white">
        <div className="text-center">
          <h1 className="text-4xl font-serif text-navy mb-4">Post Not Found</h1>
          <Link to="/insights" className="text-gold hover:text-navy transition-colors">
            ← Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  const isPublished = (post.status || '').toLowerCase() === 'published';
  const authorName = post.author?.name || 'Anu Singh';
  const authorTitle = 'CEO & Co-Founder, Zakool AI';

  return (
    <motion.article
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-off-white"
    >
      {/* ✅ Per-post SEO: canonical/OG/Twitter + Article & FAQ JSON-LD */}
      <SEO
        type="article"
        title={post.title}
        description={post.metaDescription || post.preview}
        path={`/insights/${post.slug}`}
        image={post.ogImageUrl}
        publishedTime={post.publishedAt}
        modifiedTime={post.updatedAt}
        tags={post.tags}
        jsonLd={[post.articleJsonLd, post.faqJsonLd].filter(Boolean)}
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="container-custom py-8">
          <Link
            to="/insights"
            className="inline-flex items-center text-navy hover:text-gold transition-colors mb-8"
          >
            <FiArrowLeft className="mr-2" />
            Back to Insights
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
              <span className="inline-flex items-center">
                <FiCalendar className="mr-1" />
                {post.date}
              </span>
              <span className="inline-flex items-center">
                <FiClock className="mr-1" />
                {post.readTime} min read
              </span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  isPublished ? 'bg-green-100 text-green-800' : 'bg-gold/20 text-navy'
                }`}
              >
                {post.status}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-serif text-navy mb-6">
              {post.title}
            </h1>

            {post.preview && (
              <p className="text-xl text-charcoal leading-relaxed">
                {post.preview}
              </p>
            )}
          </motion.div>
        </div>
      </div>

      {/* Content Area */}
      <div className="container-custom py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          {post.content ? (
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div
                className="
                  prose prose-lg max-w-none
                  prose-headings:font-serif
                  prose-h2:text-navy
                  prose-h3:text-navy
                  prose-a:text-navy hover:prose-a:text-gold
                  prose-li:marker:text-gold
                  prose-blockquote:border-l-gold
                  prose-p:leading-relaxed
                "
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Optional on-page FAQ section (matches faqJsonLd) */}
              {Array.isArray(post.faqs) && post.faqs.length > 0 && (
                <section className="mt-12 border-t pt-8">
                  <h2 className="text-2xl font-serif text-navy mb-4">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-4">
                    {post.faqs.map((faq, idx) => (
                      <details key={idx} className="bg-off-white rounded-md p-4">
                        <summary className="cursor-pointer font-medium text-navy">
                          {faq.question}
                        </summary>
                        <p className="mt-2 text-charcoal">{faq.answer}</p>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              <div className="border-t pt-8 mt-10 flex items-center">
                <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center text-white font-serif text-lg">
                  {authorName?.split(' ').map(s => s[0]).join('').slice(0,2) || 'AS'}
                </div>
                <div className="ml-4">
                  <p className="font-medium text-navy">{authorName}</p>
                  <p className="text-sm text-gray-600">{authorTitle}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg p-12 text-center shadow-sm">
              <div className="text-6xl mb-6">🚀</div>
              <h2 className="text-2xl font-serif text-navy mb-4">Coming Soon</h2>
              <p className="text-charcoal text-lg mb-8">
                This insight is being crafted with care. Subscribe below to be notified when it's
                published.
              </p>
              <div className="border-t pt-8 mt-8 flex items-center justify-center">
                <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center text-white font-serif text-xl">
                  AS
                </div>
                <div className="ml-4 text-left">
                  <p className="font-medium text-navy">{authorName}</p>
                  <p className="text-sm text-gray-600">Founder, Zakool</p>
                </div>
              </div>
            </div>
          )}

          {/* Related Posts */}
          <div className="mt-16">
            <h3 className="text-2xl font-serif text-navy mb-8">Other Insights</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {blogPosts
                .filter((p) => p.id !== post.id)
                .slice(0, 2)
                .map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/insights/${relatedPost.slug}`}
                    className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <h4 className="font-serif text-lg text-navy mb-2">
                      {relatedPost.title}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {(relatedPost.preview || '').substring(0, 100)}...
                    </p>
                  </Link>
                ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Newsletter */}
      <div className="bg-white py-16">
        <NewsletterSignup />
      </div>
    </motion.article>
  );
};

export default BlogPost;
